import type { CSSProperties } from 'react';

/**
 * The single-scope customer page renders in a fixed editorial palette (a warm
 * accent + near-black ink). A SINGLE-SCOPE design can restyle it — accent colour,
 * the title/price colour, and the fonts — but nothing structural. This turns a
 * design's branding into the CSS variables the single-scope components read.
 *
 * IMPORTANT: when `brand` is null (the presentation came from the tenant flag, or
 * a single-scope design left these fields blank) the variables resolve to the
 * ORIGINAL fixed values, so every existing single-scope page renders unchanged.
 * Only a customised single-scope design shifts them.
 */

/** The fixed defaults — today's single-scope look. */
const DEFAULTS = {
  accent: '#E4682F',
  accentStrong: '#C0561F',
  accentSoft: 'rgba(228,104,47,0.34)',
  primary: '#17171A',
} as const;

export interface SingleScopeBrand {
  accentColor?: string | null;
  primaryColor?: string | null;
  headingFont?: string | null;
  bodyFont?: string | null;
}

/**
 * Whether a resolved document layout is a SINGLE-SCOPE design — the one marker
 * (`branding.presentation === 'SINGLE_SCOPE'`) both estimates and proposals read
 * to switch the customer page + PDF into the single-scope presentation.
 */
export function isSingleScopeLayout(
  layout: { branding?: { presentation?: string | null } | null } | null | undefined,
): boolean {
  return layout?.branding?.presentation === 'SINGLE_SCOPE';
}

const hex = (v: string | null | undefined): string | null =>
  typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.trim()) ? v.trim() : null;

const fontStack = (v: string | null | undefined): string | null =>
  v && v.trim() ? `'${v.trim()}', Inter, -apple-system, Segoe UI, Roboto, sans-serif` : null;

/**
 * CSS custom properties for the single-scope root. Spread onto the top-level
 * container; the components consume `--ss-accent`, `--ss-accent-strong`,
 * `--ss-accent-soft`, `--ss-primary`, `--ss-heading-font`, `--ss-body-font`.
 */
export function singleScopeThemeVars(brand: SingleScopeBrand | null | undefined): CSSProperties {
  const accent = brand ? hex(brand.accentColor) : null;
  const primary = brand ? hex(brand.primaryColor) : null;
  const headingFont = brand ? fontStack(brand.headingFont) : null;
  const bodyFont = brand ? fontStack(brand.bodyFont) : null;

  // A darker accent for hover, and a 34%-alpha accent for hairline rules —
  // derived from the chosen accent, or the exact fixed values when unstyled.
  const accentStrong = accent ? `color-mix(in srgb, ${accent} 84%, #000)` : DEFAULTS.accentStrong;
  const accentSoft = accent
    ? `color-mix(in srgb, ${accent} 34%, transparent)`
    : DEFAULTS.accentSoft;

  return {
    '--ss-accent': accent ?? DEFAULTS.accent,
    '--ss-accent-strong': accentStrong,
    '--ss-accent-soft': accentSoft,
    '--ss-primary': primary ?? DEFAULTS.primary,
    '--ss-heading-font': headingFont ?? 'inherit',
    '--ss-body-font': bodyFont ?? 'inherit',
    fontFamily: bodyFont ?? undefined,
  } as CSSProperties;
}
